// @flow
import * as React from 'react';
import {
  AttemptedLeadsQuery,
  darkenHexColor,
  DataGrid,
  DataGridColumn,
  getTagLabel,
  ListPageWrapper,
  PageTitle,
  useAttemptedLeadsQuery,
  FilterBadges,
  useSessionStorageState, lightenHexColor, letterToColorfulHex
} from '../../shared';
import { Avatar, Button, Chip, Link as MuiLink, Stack } from '@mui/material';
import { Check, FunnelSimple, X } from '@phosphor-icons/react';
import { StatusView, TableSettings, useTableSettings, withTableSettings } from '../../features';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { getRoute } from '../../app/router';
import { formatToPhoneNumber } from '@coverright/ui/inputs';
import moment from 'moment';
import { useAttemptedLeadsFilterDrawer, withAttemptedLeadsFilterDrawer } from './ui/attempted-leads-filter-drawer';
import { getAgeByBirthdate, getUserName } from '@coverright/utils';
import {
  AttemptedLeadFilterInput, AttemptedLeadGroupDto,
  AttemptedLeadSortDirection,
  AttemptedLeadSortField, PageInput,
  SortDirection
} from '@coverright/data-access/types/enrollment';
import { usePartnerTags } from './hooks/use-partner-tags';
import { useMemo } from 'react';

type AttemptedLead = AttemptedLeadsQuery['attemptedLeads']['data'][0];

type AttemptedLeadFilter = AttemptedLeadFilterInput & { showPartnerLeads?: boolean | null };


const initialSort = {field: AttemptedLeadSortField.LeadDate, direction: SortDirection.Desc}

const Page = () => {
  const partnerTags = usePartnerTags();
  const [filters, setFilters] = useFilters();
  const {columns} = useTableSettings(tableColumns, filters, settings => setFilters(settings.filters || {}));
  const [page, setPage] = usePage();
  const [sort, setSort] = useSort();

  const {open} = useAttemptedLeadsFilterDrawer();

  const {loading, data} = useAttemptedLeadsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: {
        ..._.omit(filters, 'showPartnerLeads'),
        excludedTags: filters.showPartnerLeads ? [] : partnerTags.map(t => t.id)
      },
      pageInput: page,
      sort: {
        field: (sort || initialSort).field,
        direction: (sort || initialSort).direction.toUpperCase() as AttemptedLeadSortDirection,
      }
    }
  })

  const openFilters = () => open(setFilters, filters)

  const onFilterRemoveClick = (filterName: keyof AttemptedLeadFilter) => {
    setFilters(prev => {
      setPage(p => ({...p, page: 0}));
      return _.omit(prev, filterName)
    })
  };

  const filtersForBadges = useMemo(() => _.omit(filters, 'excludedTags'), [filters])

  return (
    <ListPageWrapper>
      <PageTitle title="Attempted Leads">
        <TableSettings />

        <Button variant={'outlined'}
                onClick={openFilters}
                startIcon={<FunnelSimple weight={'bold'} />}
                size={'small'} sx={{minWidth: 90, alignSelf: 'center'}}>
          Filters
        </Button>
      </PageTitle>

      <FilterBadges onRemoveFilterClick={onFilterRemoveClick} filter={filtersForBadges} nameMapFunc={filterNameFunc} />

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={data?.attemptedLeads.totalElements}
                loading={loading}
                sort={sort}
                onSortModelChange={setSort}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={data?.attemptedLeads.data || []}/>
    </ListPageWrapper>
  );
};

export const AttemptedLeads = withAttemptedLeadsFilterDrawer(withTableSettings(Page, 'attemptedLeads'));

const tableColumns: DataGridColumn<AttemptedLead, AttemptedLeadSortField>[] = [
  {
    title: 'Lead Date',
    value: v => moment(v.leadDate).format('L H:mm:ss'),
    sortKey: AttemptedLeadSortField.LeadDate
  },
  {
    title: 'Name',
    value: v => (
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <Avatar sx={{ bgcolor: lightenHexColor(letterToColorfulHex(v.email || getUserName(v)), 50),
          border: `1px solid ${darkenHexColor(letterToColorfulHex(v.email || getUserName(v)), 10)}`,
          color: darkenHexColor(letterToColorfulHex(v.email || getUserName(v)), 70), width: 20, height: 20, fontSize: 12 }}>
          {v.firstName && _.toUpper(v.firstName[0])}
        </Avatar>
        <MuiLink component={Link}
                 color={'text.primary'}
                 sx={{textDecoration: 'none'}}
                 onClick={(event: any) => event.stopPropagation()}
                 to={`/${getRoute('client')}/${v.clientId}`}>
          {getUserName(v)}
        </MuiLink>
      </Stack>
    )
  },
  {
    title: 'Phone',
    value: v => formatToPhoneNumber(v.phoneNumber)
  },
  {
    title: 'State',
    value: v => v.state
  },
  {
    title: 'Birth Date',
    value: v => v.birthDate ? moment(v.birthDate).format('L') : '',
    sortKey: AttemptedLeadSortField.Birthdate
  },
  {
    title: 'Age',
    value: v => v.birthDate ? getAgeByBirthdate(v.birthDate, 'YYYY-MM-DD') : ''
  },
  {
    title: 'Status',
    value: v => <StatusView status={v.status} />
  },
  {
    title: 'IEP flag',
    value: v => v.iepFlag ? <Check weight={'bold'} color={'green'} size={14} /> : <X weight={'bold'} color={'red'} size={14} />
  },
  {
    title: 'Called today',
    value: v => v.leadGroup === AttemptedLeadGroupDto.CalledToday ? 'Y' : 'N'
  },
  {
    title: 'Last Call Date',
    value: v => v.lastCallDate ? moment(v.lastCallDate).format('L H:mm:ss') : ''
  },
  {
    title: 'Call Agent',
    value: v => v.lastCallAgentFullName
  },
  {
    title: 'Calls Count',
    value: v => v.callsCount
  },
  {
    title: 'Urgent',
    value: v => v.urgent ? <Check weight={'bold'} color={'green'} size={14} /> : <X weight={'bold'} color={'red'} size={14} />
  },
  {
    title: 'Tags',
    value: v => <div className={'chips-wrapper'}>
      {v.tags.map(value => (
        <Chip style={{ backgroundColor: value.tag.color as string }}
              className={'ml-8 mt-5'} size="small"
              key={value.tag.id} label={getTagLabel(value)} />
      ))}
    </div>
  }
]

const filterNameFunc = (name: keyof AttemptedLeadFilter) => {
  switch (name) {
    case 'range': return 'Created';
    case 'includedTags': return 'tags';
    default: return name;
  }
}

const useFilters = () => {
  return useSessionStorageState<AttemptedLeadFilter>(
    'attemptedLeadsFilters',
    {}
  );
};

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'attemptedLeadsPage',
    { page: 0, size: 20 }
  );
};

const useSort = () => {
  return useSessionStorageState<{field: AttemptedLeadSortField, direction: SortDirection} | undefined>(
    'attemptedLeadsSort',
    initialSort
  )
}
